import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { api, apiError } from "@/lib/api";
import {
  Loader2, GraduationCap, Cpu, CheckCircle2, X, Sparkles, ArrowLeft, ArrowRight,
  MessageSquare, Send, Award, Zap, ShieldCheck, RefreshCw, Target as TargetIcon,
  Trophy,
} from "lucide-react";
import { ShareWinButton } from "@/components/app/ShareWin";

const DIFFICULTIES = [
  { id: "gentle",   label: "Gentle",    desc: "Encouraging, lower bar" },
  { id: "standard", label: "Standard",  desc: "Balanced (default)" },
  { id: "rigorous", label: "Rigorous",  desc: "Exacting, strict pass" },
];

const LEVEL_LABELS = { beginner: "Beginner", intermediate: "Intermediate", advanced: "Advanced", expert: "Expert" };
const NEXT_LEVEL = { beginner: "intermediate", intermediate: "advanced", advanced: "expert", expert: null };

// Sport-tile-style "power tips" for each AI Academy skill. Compact one-liners
// the learner can act on before they even start a lesson.
const AI_TIPS = {
  "ChatGPT": "Start every prompt with your desired OUTPUT format, not the task — 'Return a JSON array of 5…' beats 'Give me 5…'",
  "Claude": "Claude excels at long documents — paste a 50-page PDF into the context and ask for a structured, citation-heavy summary.",
  "Gemini": "Use Gemini for anything image or video — drop a screenshot in and ask 'What's wrong with this UI?' for surgical feedback.",
  "Midjourney": "Add a photographer's name + camera + lens (e.g. 'shot on Leica M11, 35mm, f/1.4') for instant photorealism.",
  "Cursor": "Use ⌘K to edit code inline — it beats copy-paste to ChatGPT because it sees your full file + project.",
  "Windsurf": "Give Cascade an END GOAL not a task — 'ship a working Stripe checkout' beats 'add a Stripe button here'.",
  "Perplexity": "Ask 'What sources disagree with X?' — it surfaces counter-evidence better than any single-model chat.",
  "Notion AI": "Turn any meeting transcript into structured minutes with '/summarize' — it detects action items automatically.",
  "GitHub Copilot": "Write your function signature + a one-line docstring FIRST — Copilot's completions get dramatically better.",
  "ElevenLabs": "Add SSML pause tags (<break time='0.6s'/>) between sentences — it makes AI voiceovers sound genuinely human.",
  "Runway": "Start with a 4-sec image-to-video generation, then extend — it's ~10x cheaper than generating 12 seconds fresh.",
  "Sora": "Camera direction words (dolly-in, whip pan, drone shot) unlock cinematic composition that generic prompts miss.",
  "Prompt Engineering": "Chain-of-thought: append 'Think step by step before answering' — accuracy jumps double digits for reasoning tasks.",
  "AI Automation": "Zapier is fastest for glue work; n8n is best for anything with loops or logic — pick by shape, not price.",
  "Vibe Coding": "Describe the OUTCOME and the CONSTRAINTS to the AI ('add feature X, don't touch file Y') — that's the whole game.",
};



export default function Learn() {
  const [d, setD] = useState(null);
  const [enrolling, setEnrolling] = useState(null);
  const [intakeFor, setIntakeFor] = useState(null); // { skill, kind, initialFocus, initialWhy }
  const [customSkill, setCustomSkill] = useState("");
  const [active, setActive] = useState(null);
  const [openLesson, setOpenLesson] = useState(null);
  const [inProgress, setInProgress] = useState([]);
  const [languagePicker, setLanguagePicker] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // When user clicks the "Languages" chip we FIRST ask which language.
  const pickSkill = (s) => {
    if ((s || "").toLowerCase() === "languages") {
      setLanguagePicker(true);
      return;
    }
    setIntakeFor({ skill: s, kind: "skill" });
  };

  // Deep-link from Goal Detail: /app/learn?help=1&skill=Complete+Business+Plan&why=...
  useEffect(() => {
    const helpSkill = searchParams.get("skill");
    if (!helpSkill) return;
    setIntakeFor({
      skill: helpSkill,
      kind: "skill",
      initialFocus: searchParams.get("focus") || helpSkill,
      initialWhy: searchParams.get("why") || "",
    });
    // Consume the params so a page refresh doesn't re-open the modal.
    const p = new URLSearchParams(searchParams);
    ["help", "skill", "focus", "why"].forEach((k) => p.delete(k));
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    const { data } = await api.get("/learn/paths");
    setD(data);
    try {
      const { data: cont } = await api.get("/learn/continue");
      setInProgress(cont.in_progress || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openPath = async (pathId) => {
    try {
      const { data } = await api.get(`/learn/paths/${pathId}`);
      setActive(data.path);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!active || active.status !== "generating") return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/learn/paths/${active.id}`);
        setActive(data.path);
        if (data.path?.status !== "generating") { load(); clearInterval(t); }
      } catch { /* ignore */ }
    }, 4000);
    return () => clearInterval(t);
  }, [active?.id, active?.status, load]);

  const enroll = async (skill, intake = null) => {
    setEnrolling(skill);
    try {
      const { data } = await api.post("/learn/enroll", { skill, level: "beginner", intake });
      setActive(data.path);
      await load();
      setIntakeFor(null);
      setCustomSkill("");
    } catch (e) { !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail)); }
    finally { setEnrolling(null); }
  };

  const startCustom = () => {
    const s = customSkill.trim();
    if (!s) { toast.error("Type a skill first."); return; }
    setIntakeFor({ skill: s, kind: "skill" });
  };

  const resumeLesson = async (item) => {
    try {
      const { data } = await api.get(`/learn/paths/${item.path_id}`);
      setActive(data.path);
      const [mi, li] = String(item.lesson_key).split("-").map((n) => parseInt(n, 10));
      const mod = data.path.curriculum?.modules?.[mi];
      const lesson = mod?.lessons?.[li];
      if (lesson) {
        setOpenLesson({ mi, li, key: item.lesson_key, ...lesson });
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!d) return <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#FF7E67]" /></div>;

  return (
    <div className="space-y-12" data-testid="learn-page">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sunrise">Academy</p>
        <h1 className="mt-3 font-nunito font-extrabold tracking-tight text-4xl text-[#2D3748] sm:text-5xl">Learn skills, master AI.</h1>
        <p className="mt-3 max-w-2xl text-[#718096]">Interactive courses with a live AI tutor. Every skill is taught, practiced, and graded — right inside b→come.</p>
        <p className="mt-2 max-w-2xl font-quicksand text-sm text-[#718096]">
          Looking for ongoing guidance on business, money, career, parenting, relationships, or mental wellness? Head to the <a href="/app/coach" className="font-semibold text-[#C2410C] underline underline-offset-2">Coach</a> section — those live as evolving conversations, not courses.
        </p>
      </div>

      {inProgress.length > 0 && (
        <div data-testid="continue-strip">
          <h2 className="mb-4 flex items-center gap-2 font-nunito text-lg font-bold tracking-tight text-[#2D3748]">
            <Sparkles className="h-5 w-5 text-[#FF7E67]" strokeWidth={2.5} /> Pick up where you left off
          </h2>
          <div className="grid gap-3 sm:grid-cols-3">
            {inProgress.map((item) => (
              <button
                key={`${item.path_id}-${item.lesson_key}`}
                onClick={() => resumeLesson(item)}
                data-testid={`resume-${item.path_id}-${item.lesson_key}`}
                className="group rounded-2xl border border-[#FFE8E3] bg-gradient-to-br from-white to-[#FFF9F5] p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#FF7E67] hover:shadow-warm"
              >
                <p className="text-[10px] font-bold uppercase tracking-widest text-[#C2410C]">
                  {item.skill} · {LEVEL_LABELS[item.level] || item.level}
                </p>
                <p className="mt-1 font-nunito text-sm font-bold text-[#2D3748] line-clamp-2">
                  {item.lesson_title}
                </p>
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-[#FFE8E3]">
                  <div className="h-full rounded-full bg-sunrise" style={{ width: `${Math.max(4, Math.round(item.scroll_pct || 0))}%` }} />
                </div>
                <p className="mt-1 font-quicksand text-[11px] text-[#718096]">
                  {Math.round(item.scroll_pct || 0)}% into this lesson · Resume →
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {d.enrolled?.length > 0 && (
        <div>
          <h2 className="mb-4 font-nunito text-lg font-bold tracking-tight text-[#2D3748]">Continue Learning</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {d.enrolled.map((p) => (
              <button key={p.id} onClick={() => openPath(p.id)} data-testid={`open-path-${p.id}`}
                className="group rounded-2xl border border-[#FFE8E3] bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:border-[#FF7E67] hover:shadow-warm">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#FF7E67]" strokeWidth={2.5} />
                  <p className="font-nunito text-base font-bold text-[#2D3748]">{p.skill}</p>
                  <span className="rounded-full bg-[#FFE8E3] px-2 py-0.5 font-nunito text-[10px] font-bold uppercase tracking-wider text-[#C2410C]">
                    {LEVEL_LABELS[p.level] || p.level}
                  </span>
                  {(p.progress || 0) >= 100 && (
                    <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#E6FBF5] px-2 py-0.5 font-nunito text-[10px] font-bold text-[#06D6A0]">
                      <Trophy className="h-3 w-3" strokeWidth={2.5} /> Mastered
                    </span>
                  )}
                  {p.status === "generating" && (
                    <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#FFF4E6] px-2 py-0.5 font-nunito text-[10px] font-bold text-[#B45309]">
                      <Loader2 className="h-3 w-3 animate-spin" /> Building
                    </span>
                  )}
                </div>
                <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-[#FFE8E3]">
                  <div className="h-full rounded-full bg-sunrise transition-all" style={{ width: `${p.progress || 0}%` }} />
                </div>
                <p className="mt-2 font-quicksand text-xs text-[#718096]">
                  {p.status === "generating" ? "Preparing your lessons…" : `${p.progress || 0}% complete`}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="mb-2 flex items-center gap-2 font-nunito text-lg font-bold tracking-tight text-[#2D3748]"><Cpu className="h-5 w-5 text-[#FF7E67]" /> AI Skills Academy</h2>
        <p className="mb-4 font-quicksand text-sm text-[#718096]">Structured, level-based mastery of every major AI tool.</p>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {d.ai_paths.map((s) => (
            <button key={s} onClick={() => setIntakeFor({ skill: s, kind: "ai" })}
              disabled={enrolling === s} data-testid={`enroll-${s}`}
              className="group flex h-full flex-col rounded-2xl border border-[#FFE8E3] bg-gradient-to-br from-white to-[#FFF9F5] p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#FF7E67] hover:shadow-warm disabled:opacity-60">
              <div className="flex items-center gap-2">
                {enrolling === s
                  ? <Loader2 className="h-4 w-4 animate-spin text-[#FF7E67]" />
                  : <Sparkles className="h-4 w-4 text-[#FF7E67]" />}
                <span className="font-nunito text-sm font-bold text-[#2D3748]">{s}</span>
              </div>
              {AI_TIPS[s] && (
                <p className="mt-2 rounded-xl bg-[#FFFBEB] p-2 font-quicksand text-[11px] leading-snug text-[#92400E]">
                  <span className="font-bold text-[#B45309]">Power tip · </span>
                  {AI_TIPS[s]}
                </p>
              )}
            </button>
          ))}
        </div>
      </div>

      <div>
        <h2 className="mb-2 flex items-center gap-2 font-nunito text-lg font-bold tracking-tight text-[#2D3748]"><GraduationCap className="h-5 w-5 text-[#FF7E67]" /> Learn a New Skill</h2>
        <p className="mb-4 font-quicksand text-sm text-[#718096]">Pick a skill — we&rsquo;ll ask what you already know and what you specifically want to focus on, then build a curriculum tailored to you.</p>
        <div className="flex flex-wrap gap-3">
          {d.categories.map((s) => (
            <button key={s} onClick={() => pickSkill(s)} disabled={enrolling === s} data-testid={`enroll-${s}`}
              className="rounded-full border border-[#FFE8E3] bg-[#FFF9F5] px-5 py-3 font-nunito text-sm font-semibold text-[#4A5568] hover:border-[#FF7E67] disabled:opacity-60">{s}</button>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3 rounded-3xl border border-[#FFE8E3] bg-white p-5">
          <p className="font-nunito text-sm font-bold text-[#2D3748]">Something else in mind?</p>
          <input value={customSkill} onChange={(e) => setCustomSkill(e.target.value)}
            placeholder="e.g. Chess openings, Italian for travel, Home renovation…" data-testid="custom-skill-input"
            onKeyDown={(e) => e.key === "Enter" && startCustom()}
            className="h-11 flex-1 min-w-[240px] rounded-full border border-[#FFE8E3] bg-[#FFF9F5] px-4 font-quicksand text-sm outline-none focus:border-[#FF7E67]" />
          <button onClick={startCustom} disabled={!customSkill.trim() || !!enrolling} data-testid="custom-skill-start-btn"
            className="inline-flex items-center gap-2 rounded-full bg-sunrise px-5 py-2.5 font-nunito text-sm font-bold text-white shadow-glow-coral hover:scale-[1.02] disabled:opacity-60">
            <Sparkles className="h-4 w-4" strokeWidth={2.5} /> Start learning
          </button>
        </div>
      </div>

      <AnimatePresence>
        {intakeFor && (
          <IntakeModal
            skill={intakeFor.skill}
            kind={intakeFor.kind}
            initialFocus={intakeFor.initialFocus}
            initialWhy={intakeFor.initialWhy}
            onClose={() => setIntakeFor(null)}
            onSubmit={(intake) => enroll(intakeFor.skill, intake)}
            saving={enrolling === intakeFor.skill} />
        )}
        {languagePicker && (
          <LanguagePickerModal
            onClose={() => setLanguagePicker(false)}
            onPick={(lang) => {
              setLanguagePicker(false);
              setIntakeFor({
                skill: lang,
                kind: "skill",
                initialFocus: `Conversational ${lang}`,
              });
            }} />
        )}
        {active && !openLesson && (
          <PathOverview path={active} onClose={() => setActive(null)}
            onLesson={(mi, li) => setOpenLesson({ mi, li, key: `${mi}-${li}` })}
            onRegenerated={(p) => { setActive(p); load(); }}
            onAdvanced={(p) => { setActive(p); load(); }} />
        )}
        {active && openLesson && (
          <LessonView path={active} lessonRef={openLesson} onBack={() => setOpenLesson(null)}
            onNav={(mi, li) => setOpenLesson({ mi, li, key: `${mi}-${li}` })}
            onDone={async () => { const { data } = await api.get(`/learn/paths/${active.id}`); setActive(data.path); load(); }} />
        )}
      </AnimatePresence>
    </div>
  );
}

function IntakeModal({ skill, kind, onClose, onSubmit, saving, initialFocus, initialWhy }) {
  const [startingFrom, setStartingFrom] = useState("");
  const [focus, setFocus] = useState(initialFocus || "");
  const [why, setWhy] = useState(initialWhy || "");
  const [timeAvail, setTimeAvail] = useState("");

  const START_OPTIONS = kind === "ai"
    ? [
        { id: "never used it", label: "I've never used it" },
        { id: "tried a few times", label: "Tried it a few times" },
        { id: "use it weekly", label: "I use it weekly" },
        { id: "use it daily but know I'm missing techniques", label: "Daily user, want to level up" },
      ]
    : [
        { id: "complete beginner", label: "Complete beginner" },
        { id: "know the basics", label: "I know the basics" },
        { id: "comfortable, want to go deeper", label: "Comfortable, want to go deeper" },
        { id: "advanced, want a refresher on specifics", label: "Advanced — need a refresher on specifics" },
      ];

  const submit = () => {
    onSubmit({
      starting_from: startingFrom || null,
      focus: focus.trim() || null,
      why: why.trim() || null,
      time_available: timeAvail || null,
    });
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-[#2D3748]/55 p-4 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="mt-10 w-full max-w-lg rounded-[2rem] bg-white p-8 shadow-warm-lg" onClick={(e) => e.stopPropagation()} data-testid="intake-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#C2410C]">Personalize your path</p>
            <h3 className="mt-2 font-nunito text-2xl font-extrabold text-[#2D3748]">{skill}</h3>
            <p className="mt-1 font-quicksand text-sm text-[#718096]">Two quick questions and your AI tutor will design a curriculum around you.</p>
          </div>
          <button onClick={onClose} data-testid="intake-close-btn" className="rounded-full bg-[#FFF9F5] p-2 text-[#718096] hover:bg-[#FFE8E3]"><X className="h-4 w-4" /></button>
        </div>

        <div className="mt-6 space-y-5">
          <div>
            <p className="mb-2 font-nunito text-sm font-bold text-[#2D3748]">Where are you starting from?</p>
            <div className="flex flex-wrap gap-2" data-testid="intake-starting-options">
              {START_OPTIONS.map((o) => (
                <button key={o.id} onClick={() => setStartingFrom(o.id)}
                  data-testid={`intake-start-${o.id.replace(/\s+/g, '-').slice(0, 24)}`}
                  className={`rounded-full border px-3.5 py-1.5 font-nunito text-xs font-bold transition-all ${
                    startingFrom === o.id
                      ? "border-[#FF7E67] bg-[#FFE8E3] text-[#C2410C] scale-105"
                      : "border-[#FFE8E3] bg-white text-[#718096] hover:border-[#FF7E67]"}`}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-2 block font-nunito text-sm font-bold text-[#2D3748]">
              What do you specifically want to learn? <span className="font-normal text-[#718096]">(the more specific, the better)</span>
            </label>
            <textarea value={focus} onChange={(e) => setFocus(e.target.value)} rows={3}
              placeholder={kind === "ai"
                ? "e.g. Better prompts for research summaries, how to write agent instructions, image generation for social media…"
                : "e.g. Conversational Spanish for restaurant travel, US residential real-estate as a first-time investor, TypeScript for React apps…"}
              data-testid="intake-focus-input"
              className="w-full resize-none rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-3 font-quicksand text-sm outline-none focus:border-[#FF7E67]" />
          </div>

          <div>
            <label className="mb-2 block font-nunito text-sm font-bold text-[#2D3748]">Why does this matter? <span className="font-normal text-[#718096]">(optional)</span></label>
            <input value={why} onChange={(e) => setWhy(e.target.value)}
              placeholder="e.g. Trip to Barcelona in 3 months"
              data-testid="intake-why-input"
              className="h-11 w-full rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 font-quicksand text-sm outline-none focus:border-[#FF7E67]" />
          </div>

          <div>
            <p className="mb-2 font-nunito text-sm font-bold text-[#2D3748]">How much time can you invest? <span className="font-normal text-[#718096]">(optional)</span></p>
            <div className="flex flex-wrap gap-2" data-testid="intake-time-options">
              {["15 min/day", "30 min/day", "1 hour/day", "A few hours/week", "Weekends only"].map((t) => (
                <button key={t} onClick={() => setTimeAvail(t)}
                  data-testid={`intake-time-${t.replace(/[^a-z0-9]/gi, '')}`}
                  className={`rounded-full border px-3 py-1.5 font-nunito text-xs font-bold transition-all ${
                    timeAvail === t ? "border-[#FF7E67] bg-[#FFE8E3] text-[#C2410C]" : "border-[#FFE8E3] bg-white text-[#718096] hover:border-[#FF7E67]"}`}>
                  {t}
                </button>
              ))}
            </div>
          </div>
        </div>

        <button onClick={submit} disabled={saving} data-testid="intake-submit-btn"
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-sunrise py-3.5 font-nunito font-bold text-white shadow-glow-coral hover:scale-[1.01] disabled:opacity-60">
          {saving
            ? <><Loader2 className="h-4 w-4 animate-spin" /> Building your path…</>
            : <><Sparkles className="h-4 w-4" strokeWidth={2.5} /> Build my curriculum</>}
        </button>
        <p className="mt-2 text-center font-quicksand text-xs text-[#A0AEC0]">Skip any question — your tutor will pick sensible defaults.</p>
      </motion.div>
    </motion.div>
  );
}

function PathOverview({ path, onClose, onLesson, onRegenerated, onAdvanced }) {
  const c = path.curriculum || {};
  const done = new Set(path.completed_lessons || []);
  const modules = c.modules || [];
  const total = modules.reduce((n, m) => n + (m.lessons?.length || 0), 0);
  const firstLesson = modules[0]?.lessons?.[0] || {};
  const isLegacy = total > 0 && !firstLesson.content;
  const isGenerating = path.status === "generating";
  const isFailed = path.status === "failed";
  const mastered = (path.progress || 0) >= 100 && total > 0;
  const nxt = NEXT_LEVEL[(path.level || "beginner").toLowerCase()];
  const [regen, setRegen] = useState(false);
  const [advancing, setAdvancing] = useState(false);

  const regenerate = async () => {
    if (!window.confirm("Regenerate this path with new interactive lessons? Your current progress will reset.")) return;
    setRegen(true);
    try {
      const { data } = await api.post(`/learn/paths/${path.id}/regenerate`);
      onRegenerated?.(data.path);
      toast.success("Building your interactive lessons — this takes about a minute.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setRegen(false); }
  };

  const advance = async () => {
    setAdvancing(true);
    try {
      const { data } = await api.post(`/learn/paths/${path.id}/advance`);
      onAdvanced?.(data.path);
      toast.success(`Unlocking ${LEVEL_LABELS[data.path.level]} · ${path.skill}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAdvancing(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-[#2D3748]/55 p-4 backdrop-blur-sm" onClick={onClose}>
      <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="mt-8 w-full max-w-3xl rounded-[2rem] bg-white p-8 shadow-warm-lg" onClick={(e) => e.stopPropagation()} data-testid="path-overview">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#C2410C]">
              Academy path · {LEVEL_LABELS[path.level] || path.level}
            </p>
            <h3 className="mt-2 font-nunito text-3xl font-extrabold tracking-tight text-[#2D3748]">{c.title || path.skill}</h3>
            <p className="mt-2 font-quicksand text-sm text-[#718096]">{c.description}</p>
          </div>
          <button onClick={onClose} data-testid="path-close-btn" className="rounded-full bg-[#FFF9F5] p-2 text-[#718096] hover:bg-[#FFE8E3]"><X className="h-4 w-4" /></button>
        </div>

        {mastered && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#06D6A0]/30 bg-[#E6FBF5] p-5" data-testid="mastered-banner">
            <div className="flex items-start gap-3">
              <Trophy className="h-6 w-6 shrink-0 text-[#06D6A0]" strokeWidth={2.5} />
              <div>
                <p className="font-nunito text-base font-bold text-[#06D6A0]">
                  You mastered {c.title || path.skill} at {LEVEL_LABELS[path.level]}!
                </p>
                <p className="font-quicksand text-xs text-[#4A5568]">
                  {nxt
                    ? `Ready to go deeper? Unlock the ${LEVEL_LABELS[nxt]} level — same skill, sharper edges.`
                    : "You've completed every level for this skill. Legend."}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <ShareWinButton testid="mastered-share-btn" label="Share this win"
                payload={{ kind: "milestone", title: `Mastered ${c.title || path.skill}`,
                           subtitle: `${LEVEL_LABELS[path.level]} level · ${total} lessons`,
                           stat: `${LEVEL_LABELS[path.level]?.toUpperCase()} · COMPLETE` }} />
              {nxt && (
                <button onClick={advance} disabled={advancing} data-testid="advance-path-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-sunrise px-4 py-2 font-nunito text-sm font-bold text-white shadow-glow-coral hover:scale-[1.02] disabled:opacity-60">
                  {advancing
                    ? <><Loader2 className="h-4 w-4 animate-spin" /> Unlocking…</>
                    : <><Sparkles className="h-4 w-4" strokeWidth={2.5} /> Continue to {LEVEL_LABELS[nxt]}</>}
                </button>
              )}
            </div>
          </div>
        )}

        {isGenerating && (
          <div className="mt-6 flex items-center gap-4 rounded-2xl border border-[#FFB347]/40 bg-[#FFF4E6] p-5" data-testid="generating-banner">
            <Loader2 className="h-6 w-6 shrink-0 animate-spin text-[#B45309]" />
            <div>
              <p className="font-nunito text-sm font-bold text-[#B45309]">Building your interactive lessons…</p>
              <p className="mt-0.5 font-quicksand text-xs text-[#718096]">Writing lesson content, examples, and practice drills. Usually 30–90 seconds.</p>
            </div>
          </div>
        )}

        {isFailed && (
          <div className="mt-6 flex items-center justify-between gap-3 rounded-2xl border border-[#FF7E67]/40 bg-[#FFE8E3] p-4" data-testid="failed-banner">
            <div>
              <p className="font-nunito text-sm font-bold text-[#B45309]">We couldn&rsquo;t build this path.</p>
              <p className="font-quicksand text-xs text-[#718096]">Give it another try — sometimes the AI needs a second attempt.</p>
            </div>
            <button onClick={regenerate} disabled={regen} data-testid="retry-generate-btn"
              className="inline-flex items-center gap-2 rounded-full bg-sunrise px-4 py-2 font-nunito text-sm font-bold text-white shadow-glow-coral hover:scale-[1.02] disabled:opacity-60">
              <RefreshCw className="h-4 w-4" strokeWidth={2.5} /> Try again
            </button>
          </div>
        )}

        {!isGenerating && !isFailed && isLegacy && (
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#FFB347]/40 bg-[#FFF4E6] p-4" data-testid="legacy-banner">
            <div>
              <p className="font-nunito text-sm font-bold text-[#B45309]">Upgrade this path to the new interactive lessons</p>
              <p className="font-quicksand text-xs text-[#718096]">Get rich lesson content, in-app practice, and a live AI tutor.</p>
            </div>
            <button onClick={regenerate} disabled={regen} data-testid="regenerate-path-btn"
              className="inline-flex items-center gap-2 rounded-full bg-sunrise px-4 py-2 font-nunito text-sm font-bold text-white shadow-glow-coral hover:scale-[1.02] disabled:opacity-60">
              {regen ? <><Loader2 className="h-4 w-4 animate-spin" /> Starting…</> : <><Sparkles className="h-4 w-4" strokeWidth={2.5} /> Upgrade lessons</>}
            </button>
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-3 font-quicksand text-xs text-[#718096]">
          {c.outcome && <span className="rounded-full bg-[#FFF4E6] px-3 py-1 font-semibold text-[#B45309]">Outcome: {c.outcome}</span>}
          {c.estimated_hours ? <span className="rounded-full bg-[#E6FBF5] px-3 py-1 font-semibold text-[#06D6A0]">~{c.estimated_hours}h</span> : null}
          {total > 0 && <span className="rounded-full bg-[#FFE8E3] px-3 py-1 font-semibold text-[#C2410C]">{done.size}/{total} done · {path.progress || 0}%</span>}
        </div>

        <div className="mt-6 space-y-6">
          {modules.map((m, mi) => (
            <div key={mi}>
              <p className="font-nunito text-sm font-bold uppercase tracking-wider text-[#FF7E67]">Module {mi + 1} · {m.title}</p>
              {m.summary && <p className="mt-1 font-quicksand text-xs text-[#718096]">{m.summary}</p>}
              <div className="mt-3 space-y-2">
                {(m.lessons || []).map((l, li) => {
                  const key = `${mi}-${li}`;
                  const complete = done.has(key);
                  return (
                    <button key={key} onClick={() => onLesson(mi, li)} data-testid={`lesson-${key}`}
                      className="flex w-full items-center justify-between gap-3 rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] p-4 text-left transition-colors hover:border-[#FF7E67] hover:bg-white">
                      <div className="min-w-0">
                        <p className="font-nunito text-sm font-bold text-[#2D3748]">{l.title}</p>
                        <p className="mt-0.5 truncate font-quicksand text-xs text-[#718096]">{l.hook}</p>
                      </div>
                      <span className="shrink-0">
                        {complete
                          ? <CheckCircle2 className="h-6 w-6 text-[#06D6A0]" strokeWidth={2.5} />
                          : <ArrowRight className="h-5 w-5 text-[#FF7E67]" strokeWidth={2.5} />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}

function LessonView({ path, lessonRef, onBack, onNav, onDone }) {
  const c = path.curriculum || {};
  const modules = c.modules || [];
  const total = modules.reduce((n, m) => n + (m.lessons?.length || 0), 0);
  const lesson = modules[lessonRef.mi]?.lessons?.[lessonRef.li];
  const [difficulty, setDifficulty] = useState("standard");
  const [answer, setAnswer] = useState("");
  const [grade, setGrade] = useState(null);
  const [grading, setGrading] = useState(false);
  const [showTutor, setShowTutor] = useState(false);
  const done = new Set(path.completed_lessons || []);
  const complete = done.has(lessonRef.key);
  const containerRef = useRef(null);
  const saveTimer = useRef(null);
  const startedAt = useRef(Date.now());

  // Reset per-lesson state when navigating.
  useEffect(() => { setAnswer(""); setGrade(null); }, [lessonRef.key]);

  // Restore saved scroll position on lesson open.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/learn/paths/${path.id}/lessons/${lessonRef.key}/progress`
        );
        const pct = data?.progress?.scroll_pct;
        if (pct && pct > 3) {
          setTimeout(() => {
            const el = containerRef.current;
            if (el) {
              const target = ((el.scrollHeight - el.clientHeight) * pct) / 100;
              el.scrollTo({ top: target, behavior: "smooth" });
              toast.success("Resumed where you left off");
            }
          }, 350);
        }
      } catch { /* no-op */ }
    })();
    startedAt.current = Date.now();
    // Save on unmount too.
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      persistProgress();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonRef.key]);

  const persistProgress = () => {
    const el = containerRef.current;
    if (!el) return;
    const denom = Math.max(1, el.scrollHeight - el.clientHeight);
    const pct = Math.round((el.scrollTop / denom) * 1000) / 10;
    if (complete) return; // Don't overwrite completion.
    api.post(`/learn/paths/${path.id}/lessons/progress`, {
      lesson_key: lessonRef.key,
      section_index: 0,
      scroll_pct: Math.max(0, Math.min(100, pct)),
      seconds: Math.round((Date.now() - startedAt.current) / 1000),
    }).catch(() => {});
  };

  const onScroll = () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(persistProgress, 1200);
  };

  if (!lesson) return null;

  const nextLesson = () => {
    const mod = modules[lessonRef.mi];
    if (lessonRef.li + 1 < (mod.lessons || []).length) return onNav(lessonRef.mi, lessonRef.li + 1);
    if (lessonRef.mi + 1 < modules.length && modules[lessonRef.mi + 1].lessons?.length) return onNav(lessonRef.mi + 1, 0);
  };

  const submit = async () => {
    if (!answer.trim()) return;
    setGrading(true);
    try {
      const { data } = await api.post(`/learn/paths/${path.id}/practice`, {
        lesson_key: lessonRef.key, answer, difficulty,
      });
      setGrade(data);
      if (data.pass) {
        toast.success(`Passed · ${data.score}/100 · ${data.progress}% path complete`);
        data.unlocked?.forEach((u) => toast.success(`Badge unlocked: ${u.title}`));
        onDone?.();
        // Path just mastered — jump back to overview so the learner sees the "Continue to next level" CTA.
        if ((data.progress || 0) >= 100) {
          toast.success("You mastered this path — a deeper level is ready to unlock.");
          setTimeout(() => onBack?.(), 600);
        }
      } else {
        toast.error(`Not passed yet · ${data.score}/100. Read the feedback and try again.`);
      }
    } catch (e) { !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail)); }
    finally { setGrading(false); }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      ref={containerRef} onScroll={onScroll}
      className="fixed inset-0 z-[70] overflow-y-auto bg-[#FFF9F5]" data-testid="lesson-view">
      <div className="mx-auto max-w-3xl px-5 py-8 lg:px-8 lg:py-10">
        <div className="flex items-center justify-between">
          <button onClick={onBack} data-testid="lesson-back-btn" className="flex items-center gap-2 font-quicksand text-sm font-semibold text-[#718096] hover:text-[#FF7E67]">
            <ArrowLeft className="h-4 w-4" /> Back to path
          </button>
          <span className="font-quicksand text-xs font-semibold text-[#718096]">
            {c.title || path.skill} · Module {lessonRef.mi + 1} · Lesson {lessonRef.li + 1} / {total}
          </span>
        </div>

        <p className="mt-6 text-xs font-semibold uppercase tracking-[0.28em] text-[#C2410C]">{modules[lessonRef.mi]?.title}</p>
        <h2 className="mt-2 font-nunito text-3xl font-extrabold tracking-tight text-[#2D3748]" data-testid="lesson-title">{lesson.title}</h2>
        {lesson.hook && <p className="mt-2 font-quicksand text-base text-[#4A5568]">{lesson.hook}</p>}

        <article className="prose mt-6 whitespace-pre-wrap rounded-3xl border border-[#FFE8E3] bg-white p-6 font-quicksand text-[15px] leading-relaxed text-[#2D3748]" data-testid="lesson-content">
          {lesson.content}
        </article>

        {lesson.examples?.length > 0 && (
          <div className="mt-5">
            <p className="mb-2 font-nunito text-sm font-bold text-[#2D3748]">Examples</p>
            <div className="space-y-2">
              {lesson.examples.map((ex, i) => (
                <div key={i} className="rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-3 font-quicksand text-sm text-[#2D3748]">{ex}</div>
              ))}
            </div>
          </div>
        )}

        {lesson.key_takeaways?.length > 0 && (
          <div className="mt-5 rounded-3xl border border-[#FFE8E3] bg-[#FFF4E6] p-5">
            <p className="mb-2 flex items-center gap-1.5 font-nunito text-sm font-bold text-[#B45309]"><Zap className="h-4 w-4" strokeWidth={2.5} /> Key takeaways</p>
            <ul className="space-y-1.5 font-quicksand text-sm text-[#2D3748]">
              {lesson.key_takeaways.map((k, i) => (<li key={i} className="flex gap-2"><span className="text-[#FF7E67]">•</span>{k}</li>))}
            </ul>
          </div>
        )}

        {/* Practice */}
        <PracticeBlock lesson={lesson} answer={answer} setAnswer={setAnswer}
          difficulty={difficulty} setDifficulty={setDifficulty}
          onSubmit={submit} grading={grading} grade={grade} complete={complete} />

        {/* Ask the Tutor drawer trigger */}
        <button onClick={() => setShowTutor(true)} data-testid="lesson-ask-tutor-btn"
          className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#FFE8E3] bg-white px-4 py-2 font-nunito text-sm font-bold text-[#C2410C] hover:bg-[#FFF9F5]">
          <MessageSquare className="h-4 w-4" strokeWidth={2.5} /> Ask the tutor
        </button>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-[#FFE8E3] pt-5">
          <button onClick={onBack} className="rounded-full border border-[#FFE8E3] px-5 py-2.5 font-nunito text-sm font-bold text-[#718096] hover:bg-white">
            Back to path
          </button>
          <button onClick={nextLesson} data-testid="lesson-next-btn"
            className="inline-flex items-center gap-2 rounded-full bg-sunrise px-5 py-2.5 font-nunito text-sm font-bold text-white shadow-glow-coral hover:scale-[1.02]">
            Next lesson <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {showTutor && <AskTutorDrawer pathId={path.id} pathTitle={c.title || path.skill} lesson={lesson}
        lessonKey={lessonRef.key} onClose={() => setShowTutor(false)} />}
    </motion.div>
  );
}

function PracticeBlock({ lesson, answer, setAnswer, difficulty, setDifficulty, onSubmit, grading, grade, complete }) {
  const p = lesson.practice || {};
  const type = p.type || "reflection";
  const isQuiz = type === "quiz";
  const isTool = type === "tool_selection";

  return (
    <div className="mt-6 rounded-3xl border-2 border-[#FF7E67]/25 bg-white p-6" data-testid="practice-block">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex items-center gap-2 font-nunito text-sm font-bold text-[#C2410C]">
          <TargetIcon className="h-4 w-4" strokeWidth={2.5} /> Practice · {labelForType(type)}
          {complete && <ShieldCheck className="h-4 w-4 text-[#06D6A0]" />}
        </p>
        {!isQuiz && (
          <div className="flex items-center gap-1.5" data-testid="difficulty-picker">
            {DIFFICULTIES.map((d) => (
              <button key={d.id} onClick={() => setDifficulty(d.id)} data-testid={`difficulty-${d.id}`}
                className={`rounded-full border px-2.5 py-1 font-nunito text-[11px] font-bold transition-all ${
                  difficulty === d.id
                    ? "border-[#FF7E67] bg-[#FFE8E3] text-[#C2410C] scale-105"
                    : "border-[#FFE8E3] bg-white text-[#718096] hover:border-[#FF7E67]"}`} title={d.desc}>
                {d.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {p.scenario && <p className="mt-3 font-quicksand text-sm text-[#4A5568]"><b>Scenario:</b> {p.scenario}</p>}
      <p className="mt-3 rounded-2xl bg-[#FFF9F5] px-4 py-3 font-quicksand text-sm text-[#2D3748]">{p.instruction}</p>
      {p.rubric && !isQuiz && (
        <p className="mt-2 font-quicksand text-xs text-[#718096]"><b>What good looks like:</b> {p.rubric}</p>
      )}

      {isQuiz || isTool ? (
        <div className="mt-4 space-y-2" data-testid="practice-options">
          {(p.options || []).map((opt, i) => (
            <button key={i} onClick={() => setAnswer(String(i))} data-testid={`practice-option-${i}`}
              className={`flex w-full items-center justify-between rounded-2xl border px-4 py-3 text-left font-quicksand text-sm transition-colors ${
                answer === String(i)
                  ? "border-[#FF7E67] bg-[#FFE8E3] text-[#C2410C]"
                  : "border-[#FFE8E3] bg-white text-[#2D3748] hover:border-[#FF7E67]"}`}>
              <span><b className="mr-2 text-[#FF7E67]">{i + 1}.</b>{opt}</span>
              {answer === String(i) && <CheckCircle2 className="h-4 w-4 text-[#FF7E67]" strokeWidth={2.5} />}
            </button>
          ))}
          {isTool && (
            <textarea value={answer.startsWith("{") ? answer : ""}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Optional — explain WHY you picked that tool (helps grading)."
              className="mt-2 w-full resize-none rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-3 font-quicksand text-sm outline-none focus:border-[#FF7E67]" rows={3}
              data-testid="practice-tool-reason" />
          )}
        </div>
      ) : (
        <textarea value={answer} onChange={(e) => setAnswer(e.target.value)}
          placeholder={type === "prompt_practice"
            ? "Write your prompt here…"
            : "Write your answer here…"} rows={6} data-testid="practice-answer-input"
          className="mt-4 w-full resize-none rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-3 font-quicksand text-sm text-[#2D3748] outline-none focus:border-[#FF7E67]" />
      )}

      <button onClick={onSubmit} disabled={grading || !answer.toString().trim()}
        data-testid="practice-submit-btn"
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-sunrise px-5 py-2.5 font-nunito text-sm font-bold text-white shadow-glow-coral transition-transform hover:scale-[1.02] disabled:opacity-60">
        {grading ? <><Loader2 className="h-4 w-4 animate-spin" /> Grading…</> : <>Submit for grading</>}
      </button>

      {grade && (
        <div className={`mt-4 rounded-2xl border p-4 ${grade.pass ? "border-[#06D6A0]/40 bg-[#E6FBF5]" : "border-[#FF7E67]/40 bg-[#FFF4E6]"}`}
          data-testid="practice-grade-result">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 font-nunito text-sm font-bold">
              {grade.pass ? <><Award className="h-4 w-4 text-[#06D6A0]" strokeWidth={2.5} /> <span className="text-[#06D6A0]">Passed</span></>
                          : <><RefreshCw className="h-4 w-4 text-[#B45309]" strokeWidth={2.5} /> <span className="text-[#B45309]">Try again</span></>}
            </p>
            <span className="rounded-full bg-white px-2.5 py-0.5 font-nunito text-xs font-bold text-[#2D3748]">{grade.score} / 100</span>
          </div>
          <p className="mt-2 font-quicksand text-sm text-[#2D3748]">{grade.feedback}</p>
          {grade.tips?.length > 0 && (
            <ul className="mt-2 space-y-1 font-quicksand text-xs text-[#4A5568]">
              {grade.tips.map((t, i) => <li key={i} className="flex gap-2"><span className="text-[#FF7E67]">→</span>{t}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function labelForType(t) {
  return { prompt_practice: "Prompt drill", tool_selection: "Pick the right tool",
           quiz: "Quiz", reflection: "Written reflection" }[t] || "Practice";
}

function AskTutorDrawer({ pathId, pathTitle, lesson, lessonKey, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const send = async () => {
    const q = input.trim();
    if (!q) return;
    setInput(""); setSending(true);
    setMessages((m) => [...m, { role: "user", content: q, id: Date.now() }]);
    try {
      const { data } = await api.post(`/learn/paths/${pathId}/ask`, {
        lesson_key: lessonKey, question: q, history: messages,
      });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, id: Date.now() + 1 }]);
    } catch (e) {
      !e.handledUpgrade && toast.error(apiError(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  return (
    <motion.aside initial={{ x: 400, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 400, opacity: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 28 }}
      className="fixed bottom-0 right-0 top-0 z-[75] flex w-full max-w-md flex-col border-l border-[#FFE8E3] bg-white shadow-warm-lg" data-testid="tutor-drawer">
      <div className="flex items-start justify-between border-b border-[#FFE8E3] p-5">
        <div>
          <p className="flex items-center gap-2 font-nunito text-sm font-bold text-[#C2410C]"><Sparkles className="h-4 w-4" strokeWidth={2.5} /> Ask the tutor</p>
          <p className="mt-0.5 font-quicksand text-xs text-[#718096]">{pathTitle} · {lesson.title}</p>
        </div>
        <button onClick={onClose} data-testid="tutor-close-btn" className="rounded-full bg-[#FFF9F5] p-1.5 text-[#718096] hover:bg-[#FFE8E3]"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4">
        {messages.length === 0 && (
          <div className="rounded-2xl bg-[#FFF9F5] p-4 font-quicksand text-sm text-[#4A5568]">
            Ask anything about this lesson — I&rsquo;ll answer using the material you&rsquo;re reading.
            Try &ldquo;can you show me another prompt example?&rdquo; or &ldquo;when should I use Claude instead?&rdquo;
          </div>
        )}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 font-quicksand text-sm ${
              m.role === "user" ? "bg-sunrise text-white" : "border border-[#FFE8E3] bg-[#FFF9F5] text-[#2D3748]"}`}>
              {m.content}
            </div>
          </div>
        ))}
        {sending && <div className="flex justify-start"><div className="rounded-2xl border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-2"><Loader2 className="h-4 w-4 animate-spin text-[#FF7E67]" /></div></div>}
        <div ref={endRef} />
      </div>
      <div className="flex gap-2 border-t border-[#FFE8E3] p-4">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Your question…" data-testid="tutor-input"
          className="h-11 flex-1 rounded-full border border-[#FFE8E3] bg-[#FFF9F5] px-4 font-quicksand text-sm outline-none focus:border-[#FF7E67]" />
        <button onClick={send} disabled={sending || !input.trim()} data-testid="tutor-send-btn"
          className="flex h-11 w-11 items-center justify-center rounded-full bg-sunrise text-white shadow-glow-coral disabled:opacity-60">
          <Send className="h-4 w-4" />
        </button>
      </div>
    </motion.aside>
  );
}


const POPULAR_LANGUAGES = [
  { code: "es", label: "Spanish", flag: "🇪🇸" },
  { code: "fr", label: "French", flag: "🇫🇷" },
  { code: "de", label: "German", flag: "🇩🇪" },
  { code: "it", label: "Italian", flag: "🇮🇹" },
  { code: "pt", label: "Portuguese", flag: "🇵🇹" },
  { code: "zh", label: "Mandarin Chinese", flag: "🇨🇳" },
  { code: "ja", label: "Japanese", flag: "🇯🇵" },
  { code: "ko", label: "Korean", flag: "🇰🇷" },
  { code: "ar", label: "Arabic", flag: "🇸🇦" },
  { code: "ru", label: "Russian", flag: "🇷🇺" },
  { code: "hi", label: "Hindi", flag: "🇮🇳" },
  { code: "nl", label: "Dutch", flag: "🇳🇱" },
  { code: "sv", label: "Swedish", flag: "🇸🇪" },
  { code: "el", label: "Greek", flag: "🇬🇷" },
  { code: "tr", label: "Turkish", flag: "🇹🇷" },
  { code: "he", label: "Hebrew", flag: "🇮🇱" },
  { code: "asl", label: "American Sign Language", flag: "🤟" },
];

function LanguagePickerModal({ onClose, onPick }) {
  const [custom, setCustom] = useState("");
  const submitCustom = () => {
    const v = custom.trim();
    if (v.length >= 2) onPick(v);
  };
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-start justify-center overflow-y-auto bg-[#2D3748]/55 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="language-picker-modal">
      <motion.div initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="mt-10 w-full max-w-xl rounded-[2rem] bg-white p-8 shadow-warm-lg"
        onClick={(e) => e.stopPropagation()}>
        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#C2410C]">Which language?</p>
        <h3 className="mt-2 font-nunito text-2xl font-extrabold text-[#2D3748]">Pick a language to learn</h3>
        <p className="mt-1 font-quicksand text-sm text-[#718096]">
          Your AI tutor will build a curriculum in that language — vocabulary, grammar drills, real dialogues, and level checks.
        </p>

        <div className="mt-5 flex flex-wrap gap-2">
          {POPULAR_LANGUAGES.map((l) => (
            <button key={l.code}
              onClick={() => onPick(l.label)}
              data-testid={`lang-${l.code}`}
              className="inline-flex items-center gap-2 rounded-full border border-[#FFE8E3] bg-[#FFF9F5] px-4 py-2 font-nunito text-sm font-semibold text-[#4A5568] transition-all hover:-translate-y-0.5 hover:border-[#FF7E67] hover:bg-white">
              <span className="text-base leading-none">{l.flag}</span> {l.label}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-2xl border border-dashed border-[#FFE8E3] bg-[#FFF9F5] p-4">
          <p className="mb-2 font-nunito text-xs font-bold uppercase tracking-wide text-[#718096]">
            Another language?
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <input value={custom} onChange={(e) => setCustom(e.target.value)}
              placeholder="e.g. Vietnamese, Swahili, Polish…"
              data-testid="lang-custom-input"
              onKeyDown={(e) => e.key === "Enter" && submitCustom()}
              className="h-11 flex-1 min-w-[220px] rounded-full border border-[#FFE8E3] bg-white px-4 font-quicksand text-sm outline-none focus:border-[#FF7E67]" />
            <button onClick={submitCustom} disabled={custom.trim().length < 2}
              data-testid="lang-custom-submit"
              className="rounded-full bg-sunrise px-5 py-2 font-nunito text-sm font-bold text-white shadow-glow-coral disabled:opacity-60">
              Continue →
            </button>
          </div>
        </div>

        <button onClick={onClose}
          className="mt-4 w-full rounded-full border border-[#FFE8E3] px-5 py-2 font-nunito text-sm font-semibold text-[#718096] hover:bg-[#FFF9F5]">
          Cancel
        </button>
      </motion.div>
    </motion.div>
  );
}

